import React from 'react';

import { useParams } from 'react-router-dom';

import { BackLink } from '../components/Chrome';
import ContentLoader from '../components/ContentLoader';
import ContentMessage from '../components/ContentMessage';
import ShadowContent from '../components/ShadowContent';
import { useNewsletter } from '../hooks/useNewsletter';
import { buildNewsletterArchivePath } from '../utils/contentPaths';

const formatDate = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const NewsletterDetail: React.FC = () => {
  const params = useParams();
  const { newsletter, html, state } = useNewsletter(params['idOrSlug'] ?? '');

  if (state === 'loading') {
    return <ContentLoader />;
  }

  if (state === 'notfound') {
    return (
      <ContentMessage
        title="Newsletter not found"
        body="The newsletter you are looking for doesn’t exist or isn’t available."
      />
    );
  }

  if (state === 'error') {
    return (
      <ContentMessage
        title="Something went wrong"
        body="This newsletter couldn’t be loaded. Please try again later."
      />
    );
  }

  const meta = [
    newsletter?.volume && `Vol ${newsletter.volume}`,
    formatDate(newsletter?.date ?? ''),
    newsletter?.categoryTitle,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className="max-w-measure py-12 sm:py-16">
      <BackLink to={buildNewsletterArchivePath()} label="All newsletters" />

      <header className="border-b border-rule-strong pb-6">
        <h1 className="font-display text-step-3 leading-[1.05] tracking-[-0.02em] text-ink">
          {newsletter?.title}
        </h1>
        {meta && <p className="mt-3 font-ui text-step--1 text-ink-muted">{meta}</p>}
      </header>

      <div className="mt-8">
        {html ? (
          // Rendered inside a shadow root so the newsletter's own <style> can't bleed into the
          // hub chrome — same style-isolation the content pages use.
          //
          // Note the consequence for the grounds: encapsulation cuts both ways, so authored
          // HTML does not inherit the live ground and keeps whatever colours its author gave
          // it. That is correct — the ground is the hub's chrome, not the document's — but it
          // does mean a white-bodied newsletter stays white on the navy ground.
          <ShadowContent html={html} />
        ) : (
          <p className="text-step-0 text-ink-muted">This newsletter has no content.</p>
        )}
      </div>
    </article>
  );
};

export default NewsletterDetail;
